import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Button
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import axios from 'axios';

const UnsubscribePage: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleUnsubscribe = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const email = params.get('email');

        if (!email) {
          setStatus('error');
          setMessage('No email address provided in the unsubscribe link.');
          return;
        }

        const response = await axios.post('/api/email/unsubscribe', { email });
        
        if (response.status === 200) {
          setStatus('success');
          setMessage('You have been successfully unsubscribed from all email notifications.');
        } else {
          throw new Error('Failed to unsubscribe');
        }
      } catch (error) {
        setStatus('error');
        setMessage('An error occurred while processing your unsubscribe request. Please try again later.');
      }
    };

    handleUnsubscribe();
  }, [location]);

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          {status === 'loading' && (
            <>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Processing Unsubscribe Request
              </Typography>
              <Typography>
                Please wait while we process your request...
              </Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Unsubscribe Successful
              </Typography>
              <Alert severity="success" sx={{ mb: 3 }}>
                {message}
              </Alert>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/')}
              >
                Return to Home
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <ErrorIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Unsubscribe Failed
              </Typography>
              <Alert severity="error" sx={{ mb: 3 }}>
                {message}
              </Alert>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/')}
              >
                Return to Home
              </Button>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default UnsubscribePage; 